import React, { useEffect, useState } from "react";
import { Share, PlusSquare, X, Download } from "lucide-react";
import { Button } from "./ui/button";
import { useBrand } from "../lib/BrandContext";

// LocalStorage keys — same names as before so a coach who previously
// dismissed the prompt stays dismissed after this simplification.
const DISMISSED_KEY = "tt_install_prompt_dismissed_at";
const SESSION_SHOWN_KEY = "tt_install_prompt_shown_session";

// Snooze duration when the user taps Dismiss / Maybe later.
const REPROMPT_AFTER_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

function isDismissedRecently() {
  try {
    const ts = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    return ts && (Date.now() - ts) < REPROMPT_AFTER_MS;
  } catch { return false; }
}

function alreadyShownThisSession() {
  try { return sessionStorage.getItem(SESSION_SHOWN_KEY) === "1"; }
  catch { return false; }
}

// Do NOT surface the install prompt on top of an open modal (Shadcn Dialog,
// AlertDialog, Sheet, etc. — all render `role="dialog"` or `role="alertdialog"`
// with `data-state="open"`). Coach might be mid-form; interrupting is rude.
function anOpenModalExists() {
  if (typeof document === "undefined") return false;
  return !!document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]');
}

/**
 * InstallPrompt — first-time Add-to-Home-Screen invitation for coaches.
 *
 * Shown when ALL of these are true:
 *   • Mobile device (iOS or Android). Desktop is skipped.
 *   • App is running in a browser tab, NOT already installed as a PWA
 *     (i.e. not in display-mode:standalone or navigator.standalone).
 *   • Coach hasn't tapped "Maybe later" in the past 14 days.
 *   • We haven't already shown this prompt in the current tab session
 *     (prevents the prompt re-mounting on every route change from
 *     re-appearing after a scroll or navigation).
 *
 * Once installed, `isStandalone()` flips true on next launch and the
 * prompt never renders again — that's the "installed" checkpoint the
 * spec asked for.
 *
 * We also refuse to render on top of an open Radix dialog (invite modal,
 * confirm dialog, etc.) — coaches mid-form shouldn't get interrupted.
 * A MutationObserver retries `tryShow` when the modal closes.
 */
export default function InstallPrompt() {
  const brand = useBrand();
  const [platform, setPlatform] = useState("desktop");
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null); // Android beforeinstallprompt event
  const visibleRef = React.useRef(false);
  // Keep the ref in sync so long-lived listeners/observers see fresh state
  // without re-registering on every visible change.
  React.useEffect(() => { visibleRef.current = visible; }, [visible]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const plat = detectPlatform();
    setPlatform(plat);

    // Mobile-only per spec (iOS/Android). Desktop coaches don't need
    // Home Screen instructions — they can install via the browser's
    // address bar icon on their own.
    if (plat === "desktop") return;
    // Never re-show once the app is installed.
    if (isStandalone()) return;
    // Honor the 14-day snooze after Maybe later.
    if (isDismissedRecently()) return;
    // Only once per tab session so a coach navigating between screens
    // doesn't see the sheet re-appear.
    if (alreadyShownThisSession()) return;

    const tryShow = () => {
      if (visibleRef.current) return;
      if (isStandalone() || isDismissedRecently()) return;
      if (alreadyShownThisSession()) return;
      // Don't interrupt an open dialog — MutationObserver below re-fires
      // us the moment the modal state clears.
      if (anOpenModalExists()) return;
      setVisible(true);
      try { sessionStorage.setItem(SESSION_SHOWN_KEY, "1"); } catch { /* ignore */ }
    };

    // Cache Android's install prompt event so we can trigger the native
    // install dialog when the coach taps our "Install app" button.
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      tryShow();
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // Cross-tab: if the user dismisses in another tab, hide here too.
    const onStorage = (e) => {
      if (e.key === DISMISSED_KEY) setVisible(false);
    };
    window.addEventListener("storage", onStorage);

    // Watch for dialogs opening/closing so we can retry once the coast is
    // clear. Debounced via requestAnimationFrame so a burst of DOM changes
    // during Radix Dialog teardown collapses into one tryShow call.
    let rafId = null;
    const observer = new MutationObserver(() => {
      if (visibleRef.current) return;
      if (rafId) return;
      rafId = requestAnimationFrame(() => { rafId = null; tryShow(); });
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state"] });

    // Try on mount immediately — the first visit is exactly when we want
    // to nudge, subject to the gates above.
    tryShow();

    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("storage", onStorage);
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
    // Empty deps — effect owns its listeners/observer for the component's
    // full lifetime. Fresh `visible` state is read via visibleRef.
  }, []);

  if (!visible || isStandalone()) return null;
  if (platform === "desktop") return null; // mobile-only per spec

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const triggerInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      // If the user tapped "install" the browser handles the rest and we
      // enter standalone mode on next launch. Either way, close the sheet.
      if (outcome === "dismissed") dismiss();
    } catch {
      /* ignore */
    }
    setDeferred(null);
    setVisible(false);
  };

  const appName = brand?.app_name || "Squad Fee";

  return (
    <div
      role="dialog"
      aria-label={`Add ${appName} to your Home Screen`}
      data-testid="install-prompt"
      className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-sm rounded-2xl border border-ink-100 bg-white p-3.5 shadow-lg"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.875rem)" }}
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute right-1.5 top-1.5 inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-500 hover:bg-ink-100"
        aria-label="Dismiss"
        data-testid="install-prompt-close"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <img
          src="/logo192.png"
          alt=""
          aria-hidden="true"
          className="h-10 w-10 shrink-0 rounded-xl bg-brand object-cover shadow-sm"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-bold text-ink-900">
            Install {appName}
          </p>
          <p className="mt-0.5 text-xs text-ink-600">
            Install {appName} and use it just like a native app.
          </p>

          {platform === "ios" ? (
            <ol className="mt-2.5 space-y-1 text-xs text-ink-700" data-testid="install-prompt-ios-steps">
              <li className="flex items-start gap-1.5">
                <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-ink-100 text-[10px] font-bold text-ink-700">1</span>
                <span>Tap the <Share className="mx-0.5 inline h-3.5 w-3.5 align-[-2px] text-brand" /> <span className="font-semibold">Share</span> button</span>
              </li>
              <li className="flex items-start gap-1.5">
                <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-ink-100 text-[10px] font-bold text-ink-700">2</span>
                <span>Select <span className="font-semibold">&ldquo;Add to Home Screen&rdquo;</span> <PlusSquare className="ml-0.5 inline h-3.5 w-3.5 align-[-2px] text-ink-500" /></span>
              </li>
              <li className="flex items-start gap-1.5">
                <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-ink-100 text-[10px] font-bold text-ink-700">3</span>
                <span>Tap <span className="font-semibold">&ldquo;Add&rdquo;</span></span>
              </li>
            </ol>
          ) : platform === "android" ? (
            deferred ? (
              <Button
                onClick={triggerInstall}
                className="mt-2.5 h-10 w-full bg-brand font-semibold text-white hover:bg-brand-hover"
                data-testid="install-prompt-android"
              >
                <Download className="mr-1 h-4 w-4" /> Install app
              </Button>
            ) : (
              <ol className="mt-2.5 space-y-1 text-xs text-ink-700" data-testid="install-prompt-android-steps">
                <li className="flex items-start gap-1.5">
                  <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-ink-100 text-[10px] font-bold text-ink-700">1</span>
                  <span>Open the browser menu</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-ink-100 text-[10px] font-bold text-ink-700">2</span>
                  <span>Select <span className="font-semibold">&ldquo;Add to Home screen&rdquo;</span> or <span className="font-semibold">&ldquo;Install app&rdquo;</span></span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-ink-100 text-[10px] font-bold text-ink-700">3</span>
                  <span>Tap <span className="font-semibold">&ldquo;Install&rdquo;</span></span>
                </li>
              </ol>
            )
          ) : null}

          <button
            type="button"
            onClick={dismiss}
            className="mt-2 text-xs font-semibold text-ink-500 hover:text-ink-900"
            data-testid="install-prompt-later"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
